import React, { useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import Flex from "../../../../../../components/Flex";
import { Form, Input, Typography, Checkbox, Radio } from "antd";
import FormGroupItem from "../FormGroupItem";
import { formatInTitleCase } from "../../../../../../util/stringUtils";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";

type EdgesPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues?: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
};

const yesOrNoRadios = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const hasDeckFieldName = "hasDeck";
const totalRaiseFieldName = "deckingTotalRaise";
const ptAFieldName = "deckingPtA";
const heightAFieldName = "deckingHeightA";
const ptBFieldName = "deckingPtB";
const heightBFieldName = "deckingHeightB";

const anchorMethodFieldName = "deckingAnchorMethod";
const anchorRadios = [
  { label: "Best way for safety", value: "best way for safety" },
  {
    label: "Anchor in front of raised area",
    value: "anchor in front of raised area",
  },
  { label: "Step Cuts", value: "step cuts" },
];

const wallHardwareFieldName = "deckingWallHardware";
const hardwareRadios = [
  { label: "Pivoting Snap Hooks", value: "pivoting snap hooks" },
  {
    label: "Pivoting O-Rings with Cablemates",
    value: "pivoting o-rings with cablemates",
  },
];

const includeCableFieldName = "deckingIncludeCable";

export default function DeckingPage({
  onNextButtonClick,
  onBackButtonClick,
  initialFormValues,
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
}: EdgesPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  const hasDeckAnswer =
    Form.useWatch(hasDeckFieldName, { form, preserve: true }) ??
    initialFormValues?.[hasDeckFieldName];
  const hasDeck = hasDeckAnswer === "yes";

  const anchorMethodAnswer =
    Form.useWatch(anchorMethodFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues?.[anchorMethodFieldName];

  const hardwareAnswer =
    Form.useWatch(wallHardwareFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues?.[wallHardwareFieldName];

  const includeCableAnswer =
    Form.useWatch(includeCableFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues?.[includeCableFieldName];

  return (
    <WizardPageWrapper
      title="Decking"
      summary={<GeneralPageSummary />}
      onNextButtonClick={onNextButtonClick}
      onBackButtonClick={onBackButtonClick}
    >
      <Flex vertical gap="16px">
        <FormGroupItem
          label="Decking"
          closeButtonText={formGroupTouched.deckingGroup ? "Save" : "Next"}
          onGroupClosed={() =>
            setFormGroupTouched({
              ...formGroupTouched,
              deckingGroup: true,
            })
          }
          notExpandedDisplayValue={hasDeck ? `Yes` : "No"}
          expandedOnMount={!formGroupTouched.deckingGroup}
        >
          <Flex vertical gap="1rem">
            <Flex vertical gap="0.5rem">
              <Typography.Text>
                Does the pool have multi-level Deck or raised areas?
              </Typography.Text>
              <Radio.Group
                options={yesOrNoRadios}
                value={hasDeckAnswer}
                onChange={(event) => {
                  const newValue = event.target.value;
                  if (newValue === "no") {
                    return form.setFieldsValue({
                      [hasDeckFieldName]: newValue,
                      [totalRaiseFieldName]: "",
                      [ptAFieldName]: "",
                      [heightAFieldName]: "",
                      [ptBFieldName]: "",
                      [heightBFieldName]: "",
                    });
                  }
                  form.setFieldValue(hasDeckFieldName, newValue);
                }}
              />
            </Flex>
            {hasDeck && (
              <>
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={totalRaiseFieldName}
                  label="Total Rise"
                >
                  <Input style={{ maxWidth: "282px", width: "100%" }} />
                </Form.Item>
                <Flex gap="1rem">
                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={ptAFieldName}
                    label="Pt"
                  >
                    <Input style={{ maxWidth: "282px", width: "100%" }} />
                  </Form.Item>

                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={heightAFieldName}
                    label="Height"
                  >
                    <Input style={{ maxWidth: "282px", width: "100%" }} />
                  </Form.Item>
                </Flex>
                <Flex gap="1rem">
                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={ptBFieldName}
                    label="Pt"
                  >
                    <Input style={{ maxWidth: "282px", width: "100%" }} />
                  </Form.Item>

                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={heightBFieldName}
                    label="Height"
                  >
                    <Input style={{ maxWidth: "282px", width: "100%" }} />
                  </Form.Item>
                </Flex>
              </>
            )}
          </Flex>
        </FormGroupItem>
        {hasDeck && formGroupTouched.deckingGroup && (
          <FormGroupItem
            label="Anchor"
            closeButtonText={
              formGroupTouched.deckingAnchorGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                deckingAnchorGroup: true,
              })
            }
            notExpandedDisplayValue={formatInTitleCase(anchorMethodAnswer)}
            expandedOnMount={!formGroupTouched.deckingAnchorGroup}
          >
            <Form.Item<CoverWizardForm>
              name={anchorMethodFieldName}
              style={{ margin: "0" }}
              label="How should we anchor?"
            >
              <Radio.Group options={anchorRadios} />
            </Form.Item>
          </FormGroupItem>
        )}
        {hasDeck && formGroupTouched.deckingAnchorGroup && (
          <FormGroupItem
            label="Hardware"
            closeButtonText={
              formGroupTouched.deckingHardwareGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                deckingHardwareGroup: true,
              })
            }
            notExpandedDisplayValue={formatInTitleCase(hardwareAnswer)}
            expandedOnMount={!formGroupTouched.deckingHardwareGroup}
          >
            <Form.Item<CoverWizardForm>
              name={wallHardwareFieldName}
              style={{ margin: "0" }}
              label="Hardware for wall application"
            >
              <Radio.Group options={hardwareRadios} />
            </Form.Item>
          </FormGroupItem>
        )}
        {hasDeck && formGroupTouched.deckingHardwareGroup && (
          <FormGroupItem
            label="Include Cable"
            closeButtonText={
              formGroupTouched.deckingCableGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                deckingCableGroup: true,
              })
            }
            notExpandedDisplayValue={
              includeCableAnswer === "yes" ? "Yes" : "No"
            }
            expandedOnMount={!formGroupTouched.deckingCableGroup}
          >
            <Form.Item<CoverWizardForm>
              name={includeCableFieldName}
              style={{ margin: "0" }}
              label="Include a cable"
            >
              <Radio.Group options={yesOrNoRadios} />
            </Form.Item>
          </FormGroupItem>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
